// src/pages/admin/logs/UserActivityLogs.js
import React, { useState, useEffect } from 'react';
import { Card, Badge } from 'react-bootstrap';
import { getUserActivityLogs, getSystemLogs, getApplicationLogs } from '../../../services/adminApi';
import LogHeader from './components/LogHeader';
import LogDetailCard from './components/LogDetailCard';
import LogTableFilters from './components/LogTableFilters';
import LogTableView from './components/LogTableView';
import './UserActivityLogs.css'; 

const UserActivityLogs = () => {
    const [activeTab, setActiveTab] = useState('system'); // 'system' | 'application'
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterLevel, setFilterLevel] = useState('All');
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
    const [selectedLog, setSelectedLog] = useState(null); // When set, renders in-page form view
    const [copied, setCopied] = useState(false);

    const fetchLogs = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                search,
                level: filterLevel !== 'All' ? filterLevel : undefined,
                actionType: filterLevel !== 'All' ? filterLevel : undefined,
                per_page: 12
            };

            let response;
            if (activeTab === 'system') {
                response = await getSystemLogs(params).catch(() => getUserActivityLogs(params));
            } else {
                response = await getApplicationLogs(params).catch(() => getUserActivityLogs(params));
            }

            const rawData = response?.data?.data || response?.data || [];
            const logsData = Array.isArray(rawData) ? rawData : [];

            setLogs(logsData);
            setPagination({
                current_page: response?.data?.current_page || 1,
                last_page: response?.data?.last_page || 1,
                total: response?.data?.total || logsData.length
            });
        } catch (err) {
            console.error('Could not load logs from server.', err);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchLogs(1);
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [search, filterLevel, activeTab]);

    const getLevelBadge = (log) => {
        if (activeTab === 'system') {
            const level = log.log_level || 'INFO';
            if (level === 'ERROR' || level === 'CRITICAL') return <Badge bg="danger" className="px-2 py-1 text-white">{level}</Badge>;
            if (level === 'WARN') return <Badge bg="warning" className="px-2 py-1 text-dark">{level}</Badge>;
            return <Badge bg="info" className="px-2 py-1 text-white">{level}</Badge>;
        } else {
            const action = log.action_type || 'GENERAL';
            if (action.includes('SUCCESS') || action.includes('APPLIED')) return <Badge bg="success" className="px-2 py-1 text-white">{action}</Badge>;
            if (action.includes('FAIL') || action.includes('REJECT')) return <Badge bg="danger" className="px-2 py-1 text-white">{action}</Badge>;
            return <Badge bg="primary" className="px-2 py-1 text-white">{action}</Badge>;
        }
    };

    return (
        <div className="container-fluid py-4 activity-logs-container">
            {/* Header Section */}
            <LogHeader 
                activeTab={activeTab} 
                setActiveTab={setActiveTab} 
                selectedLog={selectedLog} 
                setSelectedLog={setSelectedLog} 
            />

            {/* In-Page Log Detail Form View vs Standard Table View */}
            {selectedLog ? (
                <LogDetailCard 
                    selectedLog={selectedLog} 
                    setSelectedLog={setSelectedLog} 
                    activeTab={activeTab} 
                    copied={copied} 
                    setCopied={setCopied} 
                />
            ) : (
                <Card className="border-0 rounded-4 overflow-hidden mb-4 activity-logs-card">
                    <LogTableFilters 
                        activeTab={activeTab} 
                        search={search} 
                        setSearch={setSearch} 
                        filterLevel={filterLevel} 
                        setFilterLevel={setFilterLevel} 
                    />
                    <LogTableView 
                        activeTab={activeTab} 
                        loading={loading} 
                        logs={logs} 
                        pagination={pagination} 
                        getLevelBadge={getLevelBadge} 
                        setSelectedLog={setSelectedLog} 
                    />
                </Card>
            )}
        </div>
    );
};

export default UserActivityLogs;